// Revert… and Cherry-pick to… of a selected commit (spec/dashboard.md section 12.1; spec/api-conventions.md section 12.1):
// the dry run first — what the operation changes and what conflicts —, then the operation with a message. Conflicts keep
// the button disabled (they are resolved through a merge request from a branch); a protected branch refuses both.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ops } from '../../api/operations';
import type { BranchOperation, Commit } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ChangesGrid } from '../../history/ChangesGrid';
import type { ContentChange } from '../../history/changes';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Select, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { queryCache } from '../../workspace/cache';
import { CONFLICT_TEXT, PROTECTED_TEXT, conflictsOf409, errorText, shortSha, subjectOf, type ConflictLine } from './util';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import '../org/org.css';

interface DryRun {
  loading: boolean;
  error: string | null;
  result: BranchOperation | null;
}

/** Posts `{ dryRun: true }` to the operation on `branch` whenever the branch (or the commit) changes. */
function useDryRun(kind: 'revert' | 'cherry-pick', branch: string | null, sha: string): DryRun {
  const { client } = useAuth();
  const { org, game } = useGame();
  const [state, setState] = useState<DryRun>({ loading: branch !== null, error: null, result: null });
  useEffect(() => {
    if (!branch) {
      setState({ loading: false, error: null, result: null });
      return;
    }
    let live = true;
    setState({ loading: true, error: null, result: null });
    const op = kind === 'revert' ? ops.revertCommit : ops.cherryPickCommit;
    client
      .data(op, { path: { org, game: game.slug, branch, sha }, body: { dryRun: true } })
      .then((r) => live && setState({ loading: false, error: null, result: r as unknown as BranchOperation }))
      .catch((e: unknown) => live && setState({ loading: false, error: errorText(e), result: null }));
    return () => {
      live = false;
    };
  }, [kind, branch, sha, client, org, game.slug]);
  return state;
}

export function ConflictList({ conflicts }: { conflicts: ConflictLine[] }) {
  return (
    <div className="op-conflicts" data-testid="revert-conflicts" role="alert">
      <p className="error">
        {conflicts.length} conflict{conflicts.length === 1 ? '' : 's'}: {CONFLICT_TEXT}
      </p>
      <ul>
        {conflicts.map((c) => (
          <li key={c.path}>
            <code>{c.path}</code>
            {c.fields.length > 0 && <span className="muted small"> · {c.fields.join(', ')}</span>}
            {c.message && <span className="muted small"> · {c.message}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Preview({ dry, branch, nothingText }: { dry: DryRun; branch: string; nothingText: string }) {
  if (dry.loading) return <SkeletonRows rows={3} label="Running the dry run" />;
  if (dry.error)
    return (
      <LoadError>{dry.error}</LoadError>
    );
  if (!dry.result) return null;
  const conflicts = dry.result.conflicts.map((c) => ({ path: c.path, fields: c.fields }));
  return (
    <div className="op-preview" data-testid="operation-preview">
      {!dry.result.changed && conflicts.length === 0 && <p className="muted" role="note">{nothingText}</p>}
      {conflicts.length > 0 && <ConflictList conflicts={conflicts} />}
      <ChangesGrid changes={dry.result.changes as ContentChange[]} branch={branch} emptyMessage="The operation changes nothing." />
    </div>
  );
}

interface OperationDialogProps {
  open: boolean;
  onClose: () => void;
  commit: Commit;
}

export function RevertDialog({ open, onClose, commit }: OperationDialogProps) {
  return (
    <Modal title={`Revert ${shortSha(commit.sha)}`} open={open} onClose={onClose} size="l">
      {open && <RevertBody commit={commit} onClose={onClose} />}
    </Modal>
  );
}

function RevertBody({ commit, onClose }: { commit: Commit; onClose: () => void }) {
  const { client } = useAuth();
  const { org, game, branches, missing, refreshBranches } = useGame();
  const toasts = useToasts();
  const navigate = useNavigate();
  const branch = useCurrentBranch();
  const dry = useDryRun('revert', branch, commit.sha);
  const [message, setMessage] = useState(`Revert "${subjectOf(commit.message)}"`);
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState<ConflictLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isProtected = branches.find((b) => b.name === branch)?.isProtected ?? false;
  const hasConflicts = (dry.result?.conflicts.length ?? 0) > 0 || (conflicts?.length ?? 0) > 0;
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = (await client.data(ops.revertCommit, { path: { org, game: game.slug, branch, sha: commit.sha }, body: { message: message.trim() || null } })) as unknown as BranchOperation;
      if (!r.changed) toasts.info('Already reverted — nothing to do.');
      else {
        toasts.success(`Reverted ${shortSha(commit.sha)}: the new head is ${shortSha(r.commit)}.`);
        queryCache.invalidate(`branch:${game.id}:${branch}`);
        refreshBranches();
        navigate(routes.history(org, game.slug, branch, r.commit));
      }
      onClose();
    } catch (e) {
      const c = conflictsOf409(e);
      if (c) setConflicts(c);
      else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="revert-dialog" className="op-dialog">
      <p>
        Undoes exactly this commit on <strong>{branch}</strong> — <q>{subjectOf(commit.message)}</q> — and nothing after it.
      </p>
      <Preview dry={dry} branch={branch} nothingText="Already reverted — nothing to do." />
      {conflicts && <ConflictList conflicts={conflicts} />}
      {error && (
        <LoadError>{error}</LoadError>
      )}
      {!hasConflicts && (
        <Field label="Message">
          <TextInput value={message} onChange={(e) => setMessage(e.target.value)} aria-label="Message" />
        </Field>
      )}
      <div className="modal-footer form-footer op-actions">
        <span className="spacer" />
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => void submit()}
          busy={busy}
          missingPermission={missing('content_write')}
          disabled={dry.loading || !!dry.error || hasConflicts || isProtected}
          title={isProtected ? PROTECTED_TEXT : hasConflicts ? CONFLICT_TEXT : undefined}
        >
          Revert
        </Button>
      </div>
    </div>
  );
}

export function CherryPickDialog({ open, onClose, commit }: OperationDialogProps) {
  return (
    <Modal title={`Cherry-pick ${shortSha(commit.sha)}`} open={open} onClose={onClose} size="l">
      {open && <CherryPickBody commit={commit} onClose={onClose} />}
    </Modal>
  );
}

function CherryPickBody({ commit, onClose }: { commit: Commit; onClose: () => void }) {
  const { client } = useAuth();
  const { org, game, branches, missing, refreshBranches } = useGame();
  const toasts = useToasts();
  const branch = useCurrentBranch();
  const targets = branches.filter((b) => b.name !== branch);
  const [target, setTarget] = useState('');
  const dry = useDryRun('cherry-pick', target || null, commit.sha);
  const [message, setMessage] = useState(subjectOf(commit.message));
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState<ConflictLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ target: string; result: BranchOperation } | null>(null);
  const targetRow = targets.find((b) => b.name === target);
  const hasConflicts = (dry.result?.conflicts.length ?? 0) > 0 || (conflicts?.length ?? 0) > 0;
  useEffect(() => {
    setConflicts(null);
    setError(null);
    setDone(null);
  }, [target]);
  const submit = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      // an untouched message is left to the server, whose default keeps the "(cherry picked from commit <sha>)" trailer (API section 12.1)
      const typed = message.trim();
      const r = (await client.data(ops.cherryPickCommit, { path: { org, game: game.slug, branch: target, sha: commit.sha }, body: { message: typed && typed !== subjectOf(commit.message) ? typed : null } })) as unknown as BranchOperation;
      if (!r.changed) toasts.info(`Already on ${target} — nothing to do.`);
      else {
        toasts.success(`Cherry-picked ${shortSha(commit.sha)} to ${target} as ${shortSha(r.commit)}.`);
        queryCache.invalidate(`branch:${game.id}:${target}`);
        refreshBranches();
      }
      setDone({ target, result: r });
    } catch (e) {
      const c = conflictsOf409(e);
      if (c) setConflicts(c);
      else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="cherry-pick-dialog" className="op-dialog">
      <p>
        Applies this commit — <q>{subjectOf(commit.message)}</q> — onto another branch of the game.
      </p>
      <Field label="Target branch">
        <Select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Target branch">
          <option value="">Choose a branch…</option>
          {targets.map((b) => (
            <option key={b.id} value={b.name}>
              {b.name}
              {b.isProtected ? ' (protected)' : ''}
            </option>
          ))}
        </Select>
      </Field>
      {targetRow?.isProtected && (
        <p className="muted small" role="note">
          {PROTECTED_TEXT}
        </p>
      )}
      {target && !done && <Preview dry={dry} branch={target} nothingText={`Already on ${target} — nothing to do.`} />}
      {conflicts && <ConflictList conflicts={conflicts} />}
      {error && (
        <LoadError>{error}</LoadError>
      )}
      {done ? (
        <p data-testid="cherry-pick-result" role="status">
          {done.result.changed ? 'Cherry-picked. ' : `Already on ${done.target} — nothing to do. `}
          <Link to={routes.history(org, game.slug, done.target, done.result.commit)} onClick={onClose}>
            Open {done.target} at {shortSha(done.result.commit)}
          </Link>
        </p>
      ) : (
        target &&
        !hasConflicts && (
          <Field label="Message">
            <TextInput value={message} onChange={(e) => setMessage(e.target.value)} aria-label="Message" />
          </Field>
        )
      )}
      <div className="modal-footer form-footer op-actions">
        <span className="spacer" />
        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && (
          <Button
            variant="primary"
            onClick={() => void submit()}
            busy={busy}
            missingPermission={missing('content_write')}
            disabled={!target || dry.loading || !!dry.error || hasConflicts || !!targetRow?.isProtected}
            title={targetRow?.isProtected ? PROTECTED_TEXT : hasConflicts ? CONFLICT_TEXT : !target ? 'Choose the target branch' : undefined}
          >
            Cherry-pick
          </Button>
        )}
      </div>
    </div>
  );
}

/** The `:branch` of the route (decoded by react-router). */
function useCurrentBranch(): string {
  return useParams().branch ?? '';
}
